/*
 * gez-effects.js
 * ----------------------------------------------------------------------
 * Lightweight UX polish for the GreenEZ storefront. All effects are:
 *   - opt-in via DOM markers (no global state)
 *   - passive listeners (no scroll-jank)
 *   - guarded behind feature detection
 *   - respect prefers-reduced-motion
 *
 * Includes:
 *   1. Scroll-progress amber bar  (#gez-scroll-progress)
 *   2. Cart pill pulse on item count change (.gez-header__cart-pill)
 *   3. Count-up animation on .gez-counter[data-count-to] elements
 *
 * Loaded once globally from layout/theme.liquid.
 * ----------------------------------------------------------------------
 */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============================================================
  // 1. SCROLL PROGRESS BAR
  // ============================================================
  function initScrollProgress() {
    var bar = document.getElementById('gez-scroll-progress');
    if (!bar) return;

    var ticking = false;
    function update() {
      var h = document.documentElement;
      var scrollable = h.scrollHeight - h.clientHeight;
      var pct = scrollable > 0 ? (h.scrollTop / scrollable) * 100 : 0;
      bar.style.setProperty('--gez-scroll-pct', pct + '%');
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ============================================================
  // 2. CART PILL PULSE ON ITEM COUNT CHANGE
  // ============================================================
  function initCartPulse() {
    var pill = document.querySelector('.gez-header__cart-pill');
    var count = pill ? pill.querySelector('.gez-header__cart-count') : null;
    if (!pill || !count) return;

    if (reduceMotion) return;

    var observer = new MutationObserver(function () {
      pill.classList.remove('gez-pulse');
      // Force reflow so the animation re-runs even if the class is
      // re-added before the previous animation finished.
      void pill.offsetWidth;
      pill.classList.add('gez-pulse');
    });

    observer.observe(count, { childList: true, characterData: true, subtree: true });
  }

  // ============================================================
  // 3. COUNT-UP ANIMATION (IntersectionObserver-gated, fires once)
  // ============================================================
  function initCountUp() {
    var targets = document.querySelectorAll('.gez-counter[data-count-to]');
    if (!targets.length) return;
    if (reduceMotion) {
      // Just write the final value without animating.
      targets.forEach(function (el) {
        el.textContent = formatNumber(parseInt(el.dataset.countTo, 10) || 0);
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      targets.forEach(animate);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });

    function animate(el) {
      var target = parseInt(el.dataset.countTo, 10) || 0;
      var duration = parseInt(el.dataset.countDuration, 10) || 1400;
      var start = performance.now();

      function step(now) {
        var elapsed = now - start;
        var progress = Math.min(elapsed / duration, 1);
        // ease-out cubic
        var eased = 1 - Math.pow(1 - progress, 3);
        var value = Math.round(target * eased);
        el.textContent = formatNumber(value);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.textContent = formatNumber(target);
        }
      }
      window.requestAnimationFrame(step);
    }

    function formatNumber(n) {
      return n.toLocaleString('en-US');
    }
  }

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    initScrollProgress();
    initCartPulse();
    initCountUp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run count-up and cart-pulse setup after section reloads in the
  // theme editor (announcement bar text changes, etc.).
  document.addEventListener('shopify:section:load', function () {
    initCartPulse();
    initCountUp();
  });
})();
